// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Location, useLocation, useNavigate } from 'react-router-dom';
import { useContext, useEffect } from 'react';
// Components, Layouts, Pages
import { BaseInput } from '~/components/common';
// Others
import { LoadingContext } from '~/context';
import { createNewPassword } from '~/thunks/auth/authThunk';
import { VerifyTypeEnum } from '~/utils/enum/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { authRouteAbsolute } from '~/utils/routes/auth';
import { IBodyVerifyCode } from '~/utils/interface/verifyCode';
import { IBodyNewPassword, IFormCreateNewPassword } from '~/utils/interface/auth';
import { defaultValuesForm, schema } from '~/pages/common/createNewPassword/helper';
// Styles, images, icons
import styles from './CreateNewPassword.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const CreateNewPassword = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/auth']);
  const loadingContext = useContext(LoadingContext);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { state }: Location<IBodyVerifyCode> = useLocation();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<IFormCreateNewPassword>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!state?.code) navigate(`${authRouteAbsolute.forgotPassword}`);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.code]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleSubmitFormCreateNewPassword = (data: IFormCreateNewPassword) => {
    loadingContext?.show();

    const payload: IBodyNewPassword = {
      newPassword: data?.newPassword,
      code: state?.code,
    };

    dispatch(createNewPassword(payload))
      .unwrap()
      .then((resp) => {
        if (!resp.data) return;

        navigate(authRouteAbsolute.successfully, {
          state: { type: VerifyTypeEnum.FORGOT_PASSWORD },
        });
      })
      .catch((_error) => {})
      .finally(() => {
        loadingContext?.hide();
      });
  };
  //#endregion Handle Function

  return (
    <Box
      id='forgotPasswordCommonPage'
      className={cx('container')}
      component={'form'}
      onSubmit={handleSubmit(handleSubmitFormCreateNewPassword)}
    >
      <Typography className={cx('formTitle')}>{t('create_new_password_title')}</Typography>

      <Stack direction={'column'} spacing={'30px'}>
        <Stack direction={'column'} spacing={'20px'}>
          <Controller
            name='newPassword'
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                type='password'
                height={50}
                required
                label={t('create_new_password_label_new_password')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.newPassword?.message}
              />
            )}
          />

          <Controller
            name='passwordConfirm'
            control={control}
            render={({ field: { value, onChange } }) => (
              <BaseInput
                type='password'
                height={50}
                required
                label={t('create_new_password_label_confirm_password')}
                value={value}
                onChange={onChange}
                errorMessage={errors?.passwordConfirm?.message}
              />
            )}
          />
        </Stack>

        <Button variant='contained' className={cx('formBtn')} type='submit' size='large'>
          {t('create_new_password_btn_create')}
        </Button>
      </Stack>
    </Box>
  );
};

export default CreateNewPassword;
